import { useState, type FormEvent, type RefObject } from 'react';
import { useScanStream } from '@/hooks/useScanStream';
import { useGraphStore } from '@/store/graphStore';
import type { InfrastructureGraphHandle } from '@/components/InfrastructureGraph';

interface ScanFormProps {
  graphRef: RefObject<InfrastructureGraphHandle | null>;
}

export function ScanForm({ graphRef }: ScanFormProps) {
  const [inputValue, setInputValue] = useState('');
  const { startScan, scanning, error } = useScanStream(graphRef);
  const clearGraph = useGraphStore((state) => state.clearGraph);
  const scanProgress = useGraphStore((state) => state.scanProgress);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const target = inputValue.trim();
    if (!target || scanning) return;
    void startScan(target);
  };

  const handleClear = () => {
    clearGraph();
    setInputValue('');
  };

  return (
    <div className="flex flex-col gap-1">
      {/* Input row */}
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="example.com or 1.2.3.4"
          disabled={scanning}
          className="font-mono text-sm px-3 py-1.5 rounded outline-none transition-colors"
          style={{
            width: '240px',
            background: '#0d1117',
            border: '1px solid #30363d',
            color: '#c9d1d9',
            borderColor: scanning ? '#30363d' : undefined,
          }}
          onFocus={(e) => {
            (e.currentTarget as HTMLInputElement).style.borderColor = '#58a6ff';
          }}
          onBlur={(e) => {
            (e.currentTarget as HTMLInputElement).style.borderColor = '#30363d';
          }}
        />

        <button
          type="submit"
          disabled={scanning || !inputValue.trim()}
          className="px-4 py-1.5 rounded text-sm font-mono font-medium transition-colors"
          style={{
            background: scanning ? '#1c2a1c' : '#238636',
            color: scanning ? '#56d364' : '#ffffff',
            border: 'none',
            cursor: scanning ? 'not-allowed' : 'pointer',
            opacity: !inputValue.trim() && !scanning ? 0.5 : 1,
          }}
        >
          {scanning ? (
            <span className="flex items-center gap-1.5">
              <Spinner />
              Scanning...
            </span>
          ) : (
            'Scan'
          )}
        </button>

        {!scanning && (
          <button
            type="button"
            onClick={handleClear}
            className="px-3 py-1.5 rounded text-sm font-mono transition-colors"
            style={{
              background: 'transparent',
              color: '#8b949e',
              border: '1px solid #30363d',
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
        )}
      </form>

      {/* Progress bar */}
      {scanning && scanProgress != null && (
        <div style={{ width: '340px' }}>
          <div
            className="text-xs font-mono mb-0.5"
            style={{ color: '#8b949e' }}
          >
            Enriching... {scanProgress}%
          </div>
          <div
            className="rounded-full overflow-hidden"
            style={{ height: '4px', background: '#21262d' }}
          >
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${scanProgress}%`,
                background: scanProgress < 50 ? '#1f6feb' : scanProgress < 90 ? '#388bfd' : '#56d364',
              }}
            />
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div
          className="text-xs font-mono px-2 py-1 rounded"
          style={{
            color: '#ff4444',
            background: '#ff444411',
            border: '1px solid #ff444433',
            maxWidth: '340px',
          }}
        >
          Error: {error}
        </div>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <span
      className="inline-block w-3 h-3 border-2 rounded-full animate-spin"
      style={{
        borderColor: '#56d36444',
        borderTopColor: '#56d364',
      }}
    />
  );
}
